/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Session idle expiry: after no user activity for SESSION_IDLE_MS, a modal is shown.
 * User can "Stay logged in" (resets idle) or "Log out" / no response (logout and clear cache).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores";

/** Idle timeout in ms. Currently 90 min for dev convenience; use 15 * 60 * 1000 (15 min) for production. */
export const SESSION_IDLE_MS = 90 * 60 * 1000;

const CHECK_INTERVAL_MS = 10_000; // How often we check if idle time exceeded
const ACTIVITY_THROTTLE_MS = 1_000; // Throttle activity updates to once per second
/** Time user has to respond in the "session about to expire" modal before auto-logout. */
export const SESSION_EXPIRY_GRACE_MS = 30_000;
const GRACE_COUNTDOWN_SECONDS = Math.round(SESSION_EXPIRY_GRACE_MS / 1000);

/**
 * Tracks user activity. When idle >= SESSION_IDLE_MS, shows a modal: stay logged in or log out.
 * If user clicks "Stay logged in", idle timer resets. If they click "Log out" or don't respond
 * within SESSION_EXPIRY_GRACE_MS, we clear cache, logout (and Entra/MSAL when not demo), then redirect.
 */
export function useSessionExpiry() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const logoutFromMsal = useAuthStore((s) => s.logoutFromMsal);
  const isDemoMode = useAuthStore((s) => s.isDemoMode);
  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [graceSecondsRemaining, setGraceSecondsRemaining] = useState(0);
  const expiredRef = useRef(false);
  const graceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const bumpActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const doLogout = useCallback(async () => {
    if (graceTimeoutRef.current !== null) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    queryClient.clear();
    logout();
    sessionStorage.removeItem("torqe-auth");
    if (isDemoMode) {
      window.location.href = "/login";
    } else {
      // Full Entra (Microsoft) logout so user is signed out of the portal
      await logoutFromMsal();
      // If no MSAL account, logoutFromMsal returns without redirect; send to login
      window.location.href = "/login";
    }
  }, [queryClient, logout, logoutFromMsal, isDemoMode]);

  const onStayLoggedIn = useCallback(() => {
    if (graceTimeoutRef.current !== null) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    expiredRef.current = false;
    setShowExpiryModal(false);
    setGraceSecondsRemaining(0);
    bumpActivity();
  }, [bumpActivity]);

  const onLogout = useCallback(() => {
    void doLogout();
  }, [doLogout]);

  // Throttled activity handler
  const onActivity = useCallback(() => {
    if (throttleRef.current !== null) return;
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
      bumpActivity();
    }, ACTIVITY_THROTTLE_MS);
  }, [bumpActivity]);

  // Start idle timer from "now" when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) bumpActivity();
  }, [isAuthenticated, bumpActivity]);

  // Subscribe to user activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;
    events.forEach((ev) => {
      window.addEventListener(ev, onActivity);
    });
    return () => {
      events.forEach((ev) => {
        window.removeEventListener(ev, onActivity);
      });
      if (throttleRef.current !== null) {
        clearTimeout(throttleRef.current);
        throttleRef.current = null;
      }
    };
  }, [isAuthenticated, onActivity]);

  // Interval: when idle exceeded, show modal and start grace-period countdown
  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed < SESSION_IDLE_MS) return;
      if (expiredRef.current) return;

      expiredRef.current = true;
      setShowExpiryModal(true);
      setGraceSecondsRemaining(GRACE_COUNTDOWN_SECONDS);

      graceTimeoutRef.current = setTimeout(() => {
        graceTimeoutRef.current = null;
        if (countdownIntervalRef.current !== null) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        void doLogout();
      }, SESSION_EXPIRY_GRACE_MS);

      countdownIntervalRef.current = setInterval(() => {
        setGraceSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current !== null) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      if (graceTimeoutRef.current !== null) {
        clearTimeout(graceTimeoutRef.current);
        graceTimeoutRef.current = null;
      }
      if (countdownIntervalRef.current !== null) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, doLogout]);

  return {
    showExpiryModal,
    graceSecondsRemaining,
    onStayLoggedIn,
    onLogout,
  };
}
